import { Injectable, inject } from '@angular/core';
import { concatMap, map, Observable, of, throwError } from 'rxjs';
import { Auth } from '../models/auth.model';
import { ConsumerLoginApiService } from './consumer-login-api.service';
import { RedirectionUsers } from '../models/redirection-users';
import { CreateCustomerRequest, CreateCustomerResponse } from '../models/create-customer.interface';
import { HttpHeaders } from '@angular/common/http';
import { ApiResult } from '@por/shared/core';

@Injectable({
    providedIn: 'root'
})
export class AccountService {
    private readonly apiService = inject(ConsumerLoginApiService);

    processUserAccounts(email: string | undefined, authData: Auth): Observable<{ createCustomerResponse: CreateCustomerResponse | null; userAccounts: RedirectionUsers[] }> {
        if (!email || !authData) {
            return throwError(() => new Error('Invalid email or auth data'));
        }
        return this.getAccountsUsers(email).pipe(
            concatMap((contacts: RedirectionUsers[] | undefined) => {
                const userAccounts = contacts || [];

                if (userAccounts.length === 0) {
                    // Create a new customer
                    return this.createCustomer({
                        email: email,
                        companyName: authData?.CompanyName
                    }).pipe(
                        map((createCustomerResponse: CreateCustomerResponse) => ({
                            createCustomerResponse,
                            userAccounts
                        }))
                    );
                } else {
                    return of({
                        createCustomerResponse: null,
                        userAccounts
                    });
                }
            })
        );
    }

    /**
     * Get accounts Users from email
     * @param email
     * @returns Observable<RedirectionUsers[]>
     */
    private getAccountsUsers(email: string): Observable<RedirectionUsers[] | undefined> {
        const headers = new HttpHeaders({
            ...this.apiService.requestAuthHeaders()
        });
        return this.apiService
            .send<ApiResult<RedirectionUsers>>({
                verb: 'GET',
                controller: 'customer',
                method: 'find',
                apiRoute: this.apiService.cpApiRoute,
                headers,
                queryParams: {
                    email: email
                }
            })
            .pipe(map((result: ApiResult<RedirectionUsers>) => (result && result.Records ? result.Records : [])));
    }

    /**
     * Create Customer
     * @param request: CreateCustomerRequest
     * @returns  Observable<CreateCustomerResponse>
     */

    private createCustomer(request: CreateCustomerRequest): Observable<CreateCustomerResponse> {
        const headers = new HttpHeaders({
            ...this.apiService.requestAuthHeaders()
        });
        return this.apiService
            .send<ApiResult<CreateCustomerResponse>>({
                verb: 'POST',
                controller: 'customer',
                method: 'create',
                apiRoute: this.apiService.cpApiRoute,
                body: request,
                headers
            })
            .pipe(map((result: ApiResult<CreateCustomerResponse>) => result.Records as unknown as CreateCustomerResponse));
    }
}
